import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useProjectsStore } from '../store/projects';
import {
  useMarketingStore,
  ContentTemplate,
  ContentPillar,
  CreateTemplateInput,
  UpdateTemplateInput,
  ContentFormat,
} from '../store/marketing';
import { PageHeader } from '../components/PageHeader';

// TemplateIcon - matches header navigation (template/layout icon)
function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );
}

// Template type styles (labels are translated)
const TYPE_STYLES: Record<string, { color: string; bg: string }> = {
  hook: { color: 'text-purple-700', bg: 'bg-purple-100' },
  body: { color: 'text-blue-700', bg: 'bg-blue-100' },
  cta: { color: 'text-green-700', bg: 'bg-green-100' },
  full_post: { color: 'text-amber-700', bg: 'bg-amber-100' },
};

// Format icons (labels are translated)
const FORMAT_ICONS: Record<string, string> = {
  carousel: '📸',
  reel: '🎬',
  story: '📱',
  single_graphic: '🖼️',
  text: '📝',
};

// Template Modal Component
function TemplateModal({
  template,
  pillars,
  onClose,
  onSave,
  isLoading,
  t,
}: {
  template?: ContentTemplate | null;
  pillars: ContentPillar[];
  onClose: () => void;
  onSave: (data: CreateTemplateInput | UpdateTemplateInput) => Promise<void>;
  isLoading: boolean;
  t: (key: string) => string;
}) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [templateType, setTemplateType] = useState<'hook' | 'body' | 'cta' | 'full_post'>(
    template?.template_type || 'hook'
  );
  const [format, setFormat] = useState<ContentFormat>(template?.format || 'carousel');
  const [pillarId, setPillarId] = useState<string>(template?.pillar_id || '');
  const [contentText, setContentText] = useState(
    template?.content?.text ? String(template.content.text) : ''
  );
  const [variablesText, setVariablesText] = useState(
    template?.variables?.join(', ') || ''
  );
  const [exampleOutput, setExampleOutput] = useState(template?.example_output || '');
  const [usageNotes, setUsageNotes] = useState(template?.usage_notes || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      name,
      description: description || undefined,
      template_type: templateType,
      format,
      pillar_id: pillarId || undefined,
      content: { text: contentText },
      variables: variablesText.split(',').map((v) => v.trim()).filter(Boolean),
      example_output: exampleOutput || undefined,
      usage_notes: usageNotes || undefined,
    });
  };

  // Build type options with translated labels
  const typeOptions = Object.keys(TYPE_STYLES).map((key) => ({
    value: key,
    label: t(`marketing:templates.types.${key}`),
  }));

  // Build format options with translated labels
  const formatOptions = Object.entries(FORMAT_ICONS).map(([value, icon]) => ({
    value,
    label: t(`marketing:formats.${value}`),
    icon,
  }));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl transform transition-all max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {template ? t('marketing:templates.editTemplate') : t('marketing:templates.newTemplate')}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:templates.nameRequired')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('marketing:templates.namePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:templates.description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('marketing:templates.descriptionPlaceholder')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Type, Format, Pillar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('marketing:templates.type')}
                </label>
                <select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value as 'hook' | 'body' | 'cta' | 'full_post')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('marketing:templates.format')}
                </label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as ContentFormat)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {formatOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.icon} {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('marketing:templates.pillar')}
                </label>
                <select
                  value={pillarId}
                  onChange={(e) => setPillarId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">{t('marketing:templates.allPillars')}</option>
                  {pillars.map((pillar) => (
                    <option key={pillar.id} value={pillar.id}>
                      {pillar.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Template Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:templates.templateTextRequired')}
              </label>
              <textarea
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                placeholder={t('marketing:templates.templateTextPlaceholder')}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('marketing:templates.variableHint')}
              </p>
            </div>

            {/* Variables */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:templates.variables')}
              </label>
              <input
                type="text"
                value={variablesText}
                onChange={(e) => setVariablesText(e.target.value)}
                placeholder={t('marketing:templates.variablesPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Example Output */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:templates.exampleOutput')}
              </label>
              <textarea
                value={exampleOutput}
                onChange={(e) => setExampleOutput(e.target.value)}
                placeholder={t('marketing:templates.exampleOutputPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Usage Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:templates.usageNotes')}
              </label>
              <textarea
                value={usageNotes}
                onChange={(e) => setUsageNotes(e.target.value)}
                placeholder={t('marketing:templates.usageNotesPlaceholder')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim() || !contentText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? t('common:saving') : template ? t('common:save') : t('common:create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Apply Template Modal - Variablen ausfüllen und Text kopieren
function ApplyModal({
  template,
  onClose,
  t,
}: {
  template: ContentTemplate;
  onClose: () => void;
  t: (key: string) => string;
}) {
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);

  // Initialize variables with empty strings
  useEffect(() => {
    if (template.variables && template.variables.length > 0) {
      const initial: Record<string, string> = {};
      template.variables.forEach((v) => {
        initial[v] = '';
      });
      setVariables(initial);
    }
  }, [template.variables]);

  // Apply variables to template text
  const getAppliedText = () => {
    let text = template.content?.text ? String(template.content.text) : '';

    // Also check for hook, body, cta in content object
    if (template.content?.hook) text += template.content.hook + '\n';
    if (template.content?.body) text += template.content.body + '\n';
    if (template.content?.cta) text += template.content.cta;

    // Replace variables
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      text = text.replace(regex, value || `{{${key}}}`);
    });

    return text.trim();
  };

  // Also get applied example output
  const getAppliedExample = () => {
    if (!template.example_output) return '';
    let text = template.example_output;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      text = text.replace(regex, value || `{{${key}}}`);
    });
    return text;
  };

  const handleCopy = async () => {
    const text = getAppliedText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const hasVariables = template.variables && template.variables.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl transform transition-all max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('marketing:templates.applyTemplate')}: {template.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Variables Input */}
            {hasVariables && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">{t('marketing:templates.fillVariables')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {template.variables?.map((v) => (
                    <div key={v}>
                      <label className="block text-xs text-gray-500 mb-1 font-mono">
                        {`{{${v}}}`}
                      </label>
                      <input
                        type="text"
                        value={variables[v] || ''}
                        onChange={(e) => setVariables({ ...variables, [v]: e.target.value })}
                        placeholder={t('marketing:templates.valueFor').replace('{{variable}}', v)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Result Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">{t('marketing:templates.result')}</h4>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    copied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('marketing:templates.copied')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {t('marketing:templates.copy')}
                    </>
                  )}
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                  {getAppliedText() || t('marketing:templates.noText')}
                </pre>
              </div>
            </div>

            {/* Example Output if available */}
            {template.example_output && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('marketing:templates.exampleOutput')}</h4>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <pre className="whitespace-pre-wrap text-sm text-blue-800 font-sans">
                    {getAppliedExample()}
                  </pre>
                </div>
              </div>
            )}

            {/* Usage Notes */}
            {template.usage_notes && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>{t('marketing:templates.note')}</strong> {template.usage_notes}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('common:close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteModal({
  template,
  onClose,
  onConfirm,
  isLoading,
  t,
}: {
  template: ContentTemplate;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  t: (key: string) => string;
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('marketing:templates.deleteTemplateConfirm')}</h3>
              <p className="text-sm text-gray-500">
                "{template.name}" {t('marketing:templates.willBeDeleted')}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? t('common:deleting') : t('common:delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MarketingTemplatesPage() {
  const { t } = useTranslation(['marketing', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const {
    templates,
    pillars,
    isLoading,
    error,
    fetchTemplates,
    fetchPillars,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    clearError,
  } = useMarketingStore();

  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ContentTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<ContentTemplate | null>(null);
  const [applyingTemplate, setApplyingTemplate] = useState<ContentTemplate | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [formatFilter, setFormatFilter] = useState<string>('');
  const [pillarFilter, setPillarFilter] = useState<string>('');

  // Build type options with translated labels
  const typeOptions = Object.keys(TYPE_STYLES).map((key) => ({
    value: key,
    label: t(`marketing:templates.types.${key}`),
    ...TYPE_STYLES[key],
  }));

  // Build format options with translated labels
  const formatOptions = Object.entries(FORMAT_ICONS).map(([value, icon]) => ({
    value,
    label: t(`marketing:formats.${value}`),
    icon,
  }));

  // Auth check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProjects();
  }, [isAuthenticated, navigate, fetchProjects]);

  // Set project from URL or default
  useEffect(() => {
    const projectIdParam = searchParams.get('project_id');
    if (projectIdParam && projects.some((p) => p.id === projectIdParam)) {
      setSelectedProjectId(projectIdParam);
    } else if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [searchParams, projects, selectedProjectId, setSelectedProjectId]);

  // Load data
  useEffect(() => {
    if (selectedProjectId) {
      fetchPillars(selectedProjectId);
      fetchTemplates(selectedProjectId, {
        pillarId: pillarFilter || undefined,
        type: typeFilter || undefined,
        format: formatFilter || undefined,
      });
    }
  }, [selectedProjectId, typeFilter, formatFilter, pillarFilter, fetchPillars, fetchTemplates]);

  const handleCreateTemplate = async (data: CreateTemplateInput | UpdateTemplateInput) => {
    if (!selectedProjectId) return;
    await createTemplate({
      ...data,
      project_id: selectedProjectId,
    } as CreateTemplateInput);
    setShowModal(false);
  };

  const handleUpdateTemplate = async (data: UpdateTemplateInput) => {
    if (!editingTemplate) return;
    await updateTemplate(editingTemplate.id, data);
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate) return;
    await deleteTemplate(deletingTemplate.id);
    setDeletingTemplate(null);
  };

  const handleDuplicate = async (template: ContentTemplate) => {
    await duplicateTemplate(template.id);
  };

  const handleToggleActive = async (template: ContentTemplate) => {
    await updateTemplate(template.id, { is_active: !template.is_active });
  };

  const getPillar = (pillarId: string | null): ContentPillar | undefined => {
    return pillars.find((p) => p.id === pillarId);
  };

  const getTypeInfo = (type: string) => {
    return {
      label: t(`marketing:templates.types.${type}`),
      ...TYPE_STYLES[type],
    };
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        icon={<TemplateIcon className="w-7 h-7" />}
        title={t('marketing:templates.title')}
        subtitle={t('marketing:templates.subtitle')}
        breadcrumbs={[{ label: t('marketing:breadcrumbs.marketing'), href: '/marketing' }]}
        actions={
          <div className="flex items-center gap-3">
            {/* Project selector */}
            <select
              value={selectedProjectId || ''}
              onChange={(e) => {
                setSelectedProjectId(e.target.value);
                navigate(`/marketing/templates?project_id=${e.target.value}`);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('marketing:templates.newTemplate')}
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
        >
          <option value="">{t('marketing:templates.allTypes')}</option>
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={formatFilter}
          onChange={(e) => setFormatFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
        >
          <option value="">{t('marketing:templates.allFormats')}</option>
          {formatOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.icon} {opt.label}
            </option>
          ))}
        </select>

        <select
          value={pillarFilter}
          onChange={(e) => setPillarFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
        >
          <option value="">{t('marketing:templates.allPillars')}</option>
          {pillars.map((pillar) => (
            <option key={pillar.id} value={pillar.id}>
              {pillar.name}
            </option>
          ))}
        </select>

        {(typeFilter || formatFilter || pillarFilter) && (
          <button
            onClick={() => {
              setTypeFilter('');
              setFormatFilter('');
              setPillarFilter('');
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {t('marketing:templates.resetFilters')}
          </button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">{t('common:loading')}</div>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('marketing:templates.empty.title')}
          </h3>
          <p className="text-gray-500 mb-4">
            {t('marketing:templates.empty.description')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('marketing:templates.createFirst')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const pillar = getPillar(template.pillar_id);
            const typeInfo = getTypeInfo(template.template_type);
            const formatInfo = formatOptions.find((f) => f.value === template.format);

            return (
              <div
                key={template.id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all hover:shadow-md ${
                  template.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
                }`}
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <span className="text-xs text-gray-400">{template.display_id}</span>
                    </div>

                    {/* Actions Menu */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setApplyingTemplate(template)}
                        className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                        title={t('marketing:templates.apply')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title={t('marketing:templates.duplicate')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setEditingTemplate(template)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title={t('common:edit')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleToggleActive(template)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title={template.is_active ? t('marketing:pillars.deactivate') : t('marketing:pillars.activate')}
                      >
                        {template.is_active ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => setDeletingTemplate(template)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title={t('common:delete')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${typeInfo?.bg} ${typeInfo?.color}`}>
                      {typeInfo?.label}
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      {formatInfo?.icon} {formatInfo?.label}
                    </span>
                    {pillar && (
                      <span
                        className="px-2 py-0.5 text-xs rounded text-white"
                        style={{ backgroundColor: pillar.color }}
                      >
                        {pillar.name}
                      </span>
                    )}
                    {!template.is_active && (
                      <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-500 rounded">
                        {t('marketing:pillars.inactive')}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {template.description}
                    </p>
                  )}

                  {/* Content Preview */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="text-xs text-gray-500 font-mono line-clamp-3">
                      {template.content?.text ? String(template.content.text) : '-'}
                    </p>
                  </div>

                  {/* Variables */}
                  {template.variables && template.variables.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.variables.map((v, i) => (
                        <span key={i} className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded font-mono">
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    <span>{t('marketing:templates.usedTimes').replace('{{count}}', String(template.use_count))}</span>
                    {template.last_used_at && (
                      <span>
                        {t('marketing:templates.lastUsed').replace('{{date}}', new Date(template.last_used_at).toLocaleDateString('de-DE'))}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <TemplateModal
          pillars={pillars}
          onClose={() => setShowModal(false)}
          onSave={handleCreateTemplate}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <TemplateModal
          template={editingTemplate}
          pillars={pillars}
          onClose={() => setEditingTemplate(null)}
          onSave={handleUpdateTemplate}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Delete Modal */}
      {deletingTemplate && (
        <DeleteModal
          template={deletingTemplate}
          onClose={() => setDeletingTemplate(null)}
          onConfirm={handleDeleteTemplate}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Apply Modal */}
      {applyingTemplate && (
        <ApplyModal
          template={applyingTemplate}
          onClose={() => setApplyingTemplate(null)}
          t={t}
        />
      )}
    </div>
  );
}
